// src/components/order/EsimListTable.tsx
//
// Order items (eSIM) table
// - Keeps the Search UI (Search / Clear / Export) as-is (Export is part of the Search UI concept).
// - Does NOT use StandardTableToolbar (top toolbar), but keeps StandardPagination.
// - Uses StandardTableFrame.
// - Displays empty values consistently as "-".
// - Keeps refund / usage / full refund modals intact.

import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  useTheme,
  Button,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';
import {
  Replay,
  Insights,
  QrCode2,
  Search as SearchIcon,
  Download as DownloadIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';

import { useRecoilValue } from 'recoil';
import { BarLoader } from 'react-spinners';
import { utcToZonedTime } from 'date-fns-tz';

import Modal from '@components/common/Modal';
import EsimQrCodeDialog from '@components/common/EsimQrCodeDialog';
import RefundEsimContent from './RefundEsimContent';
import FullRefundEsimContent from './FullRefundEsimContent';
import { OrderEsimUsageContent } from './OrderEsimUsageContent';

import { useOrderEsims } from '@queries/orders/useOrderEsims';
import { useOrderEsimsCount } from '@queries/orders/useOrderEsimsCount';
import { useStockDetailForPartner } from '@queries/stocks/useStockDetailForPartner';

import {
  exportOrderDetailEsimsExcel,
  type GetOrderEsimsParams,
  type OrderEsimResponse,
  type OrderParameterType,
  type OrderResponse,
  type PassportData,
} from '@apis/order';

import { downloadBlob } from '@utils/file';
import { timestampToDate } from '@utils/time';
import { KOREAN_TIME_ZONE } from '@constants/timezone';

import { userState } from '@recoils/atoms';
import { UserGroup } from '@models/user';
import { EsimStatus, EsimType } from '@models/stock';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import { StandardTableFrame, StandardPagination, cellSx } from '@components/common/table';

const PAGE_SIZE = 50;

type EsimListTableProps = {
  isRefundable: boolean;
  order: OrderResponse;
  fullRefund: boolean;
  setFullRefund: React.Dispatch<React.SetStateAction<boolean>>;
  onRefund: () => void;
  /**
   * When true, hides the search + table UI,
   * but keeps refund/status logic (modals) alive.
   * Used for reservation orders where only full refund is needed.
   */
  hideList?: boolean;
};

const SEARCH_TYPE: Record<EsimType, OrderParameterType[]> = {
  [EsimType.LPA]: ['LPA_URL', 'ICCID', 'MSISDN'],
  [EsimType.REDEMPTION]: ['REDEMPTION_CODE', 'LPA_URL', 'ICCID', 'MSISDN'],
  [EsimType.DISCOVERY]: ['IMEI', 'EID', 'ACTIVATION_DATE'],
  [EsimType.PSIM_TOPUP]: ['ICCID'],
};

const PARAMETER_LABEL: Partial<Record<OrderParameterType, string>> = {
  LPA_URL: 'Activation Code',
  ICCID: 'ICCID',
  MSISDN: 'MSISDN',
  REDEMPTION_CODE: 'Redemption code',
  IMEI: 'IMEI',
  EID: 'EID',
  ACTIVATION_DATE: 'Activation date',
};

const isActivationDateField = (field: OrderParameterType) => field === 'ACTIVATION_DATE';

type CellValue = string | number | null | undefined;

const renderValue = (value: CellValue): React.ReactNode => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const isNonEmptyString = (v: unknown): v is string =>
  typeof v === 'string' && v.trim().length > 0;

const EsimListTable = ({
  isRefundable,
  order,
  fullRefund,
  setFullRefund,
  onRefund,
  hideList = false,
}: EsimListTableProps) => {
  const theme = useTheme();
  const user = useRecoilValue(userState);
  const systemAlert = useSystemAlert();

  const { data: stock } = useStockDetailForPartner(order.stock.stockId);

  const buildDefaultSearchForm = useCallback((): GetOrderEsimsParams => {
    const esimType = order.stock.esimType;
    return {
      esimType,
      parameterType: SEARCH_TYPE[esimType][0],
      parameterValue: '',
    };
  }, [order.stock.esimType]);

  const [searchForm, setSearchForm] = useState<GetOrderEsimsParams>(() => buildDefaultSearchForm());
  const [page, setPage] = useState(1);

  const {
    data,
    refetch,
    isLoading: isListLoading = false,
  } = useOrderEsims({
    orderId: order.orderId,
    page,
    searchParams: searchForm,
  });

  const {
    data: totalCount,
    refetch: refetchCount,
    isLoading: isCountLoading = false,
  } = useOrderEsimsCount(order.orderId, searchForm);

  const total = totalCount ?? 0;

  const [selectedEsim, setSelectedEsim] = useState<OrderEsimResponse | null>(null);

  const [isExporting, setExporting] = useState(false);
  const [partialRefund, setPartialRefund] = useState(false);
  const [usagePopupVisible, setUsagePopupVisible] = useState(false);
  const [qrEsim, setQrEsim] = useState<OrderEsimResponse | null>(null);

  const handleOnSearch = useCallback(() => {
    setPage(1);
    refetch();
    refetchCount();
  }, [refetch, refetchCount]);

  const handleOnExport = useCallback(async () => {
    if (isExporting) return;

    setExporting(true);
    try {
      const res = await exportOrderDetailEsimsExcel(order.orderId, searchForm);
      const kstDate = utcToZonedTime(new Date(), KOREAN_TIME_ZONE);
      const fileName = `esim-inventories-${kstDate.toISOString()}.xlsx`;
      downloadBlob(res, fileName);
    } finally {
      setExporting(false);
    }
  }, [isExporting, order.orderId, searchForm]);

  const handleRefund = useCallback((esim: OrderEsimResponse) => {
    setSelectedEsim(esim);
    setPartialRefund(true);
  }, []);

  const handleCopyQrValue = useCallback(
    async (label: string, value: string) => {
      try {
        await navigator.clipboard.writeText(value);
        systemAlert.success(`${label} copied.`);
      } catch {
        systemAlert.error('Failed to copy.');
      }
    },
    [systemAlert],
  );

  const renderActivationCodeCell = useCallback((esim: OrderEsimResponse) => {
    const hasLpaUrl = isNonEmptyString(esim.lpaUrl);

    return (
      <TableCell sx={cellSx.mono}>
        <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
          <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {renderValue(esim.lpaUrl)}
          </Box>
          {hasLpaUrl && (
            <Tooltip title="Show QR (Activation Code)">
              <IconButton
                size="small"
                onClick={() => setQrEsim(esim)}
                aria-label="Show QR code"
              >
                <QrCode2 fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    );
  }, []);

  const rows = useMemo<OrderEsimResponse[]>(
    () => (data?.data ?? []),
    [data?.data],
  );

  const hasPassport = useMemo(() => {
    return rows
      .map((esim) => esim.customerDetail?.passportData)
      .filter((passport): passport is PassportData => Boolean(passport))
      .some(
        (passport) =>
          Boolean(passport.passportBirthdate) ||
          Boolean(passport.passportCountry) ||
          Boolean(passport.passportGender) ||
          Boolean(passport.passportName) ||
          Boolean(passport.passportNumber),
      );
  }, [rows]);

  const supplierName = stock?.data?.[0]?.supplier?.supplierName ?? '';
  const showUsageColumn = ['ORANGE', 'CMI', "LG U+", "Joytel(Redeem)", "BillionConnect(API)"].includes(supplierName);
  const showRefundColumn = user?.group !== UserGroup.PARTNER;

  const isLoading = Boolean(isListLoading || isCountLoading);

  // StandardTableFrame requires an explicit column count.
  const columnsCount = useMemo(() => {
    let base = 1; // Index

    const t = searchForm.esimType;
    if (t === EsimType.LPA) base += 10 + (hasPassport ? 5 : 0);
    if (t === EsimType.REDEMPTION) base += 11 + (hasPassport ? 5 : 0);
    if (t === EsimType.DISCOVERY) base += 5 + (hasPassport ? 5 : 0);
    if (t === EsimType.PSIM_TOPUP) base += 7;

    if (showUsageColumn) base += 1;
    if (showRefundColumn) base += 1;

    return base;
  }, [hasPassport, searchForm.esimType, showRefundColumn, showUsageColumn]);

  const handleChangePage = useCallback((next: number) => {
    setPage(next);
  }, []);

  const handleClear = useCallback(() => {
    const nextForm = buildDefaultSearchForm();
    setSearchForm(nextForm);
    setPage(1);

    // Trigger refetch after state updates so queries use the cleared params.
    queueMicrotask(() => {
      refetch();
      refetchCount();
    });
  }, [buildDefaultSearchForm, refetch, refetchCount]);

  return (
    <>
      {!hideList && (
        <Card
          elevation={0}
          sx={{
            mt: 4,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardHeader
            title={
              <Typography variant="h6" fontWeight={700}>
                Order Items
              </Typography>
            }
            subheader={
              <Typography variant="body2" color="text.secondary">
                {total.toLocaleString()} in total
              </Typography>
            }
          />

          <CardContent>
            {/* Search section (Export is part of Search UI) */}
            <Grid container spacing={2} alignItems="flex-end" sx={{ mt: 0.5, mb: 1 }}>
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="esim-search-field-label">Search by</InputLabel>
                  <MuiSelect
                    labelId="esim-search-field-label"
                    label="Search by"
                    value={searchForm.parameterType}
                    onChange={(e) =>
                      setSearchForm((prev) => ({
                        ...prev,
                        parameterType: e.target.value as OrderParameterType,
                      }))
                    }
                  >
                    {SEARCH_TYPE[searchForm.esimType].map((type) => (
                      <MenuItem key={type} value={type}>
                        {PARAMETER_LABEL[type] ?? type}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={5} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type={isActivationDateField(searchForm.parameterType) ? 'date' : 'text'}
                  value={searchForm.parameterValue}
                  onChange={(e) =>
                    setSearchForm((prev) => ({
                      ...prev,
                      parameterValue: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleOnSearch();
                  }}
                  label={isActivationDateField(searchForm.parameterType) ? 'Activation date' : 'Keyword'}
                  placeholder={isActivationDateField(searchForm.parameterType) ? 'Select date' : 'Enter keyword'}
                  InputLabelProps={isActivationDateField(searchForm.parameterType) ? { shrink: true } : undefined}
                />
              </Grid>

              <Grid item xs={12} sm={3} md={5}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5} flexWrap="wrap">
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button variant="contained" size="small" startIcon={<SearchIcon />} onClick={handleOnSearch}>
                      Search
                    </Button>

                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      startIcon={<RestartAltIcon />}
                      onClick={handleClear}
                    >
                      Clear
                    </Button>
                  </Box>

                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleOnExport}
                    disabled={isExporting}
                  >
                    Export
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {isExporting && (
              <Box mb={2}>
                <BarLoader loading={isExporting} width="100%" height={6} />
              </Box>
            )}

            {/* Table */}
            <Box mt={1}>
              <StandardTableFrame<OrderEsimResponse>
                head={
                  <TableRow>
                    <TableCell width={70} align="right">
                      #
                    </TableCell>

                    {searchForm.esimType === EsimType.LPA && (
                      <>
                        <TableCell>Serial No.</TableCell>
                        <TableCell>ICCID</TableCell>
                        <TableCell>IMSI</TableCell>
                        <TableCell>MSISDN</TableCell>
                        <TableCell>Activation Code</TableCell>
                        <TableCell>Confirm Code</TableCell>
                        <TableCell>Ext. eSIM ID</TableCell>
                        <TableCell>PIN</TableCell>
                        <TableCell>PUK</TableCell>
                        <TableCell>Expiry</TableCell>
                        {hasPassport && (
                          <>
                            <TableCell>P. Country</TableCell>
                            <TableCell>P. Name</TableCell>
                            <TableCell>P. Birthdate</TableCell>
                            <TableCell>P. Gender</TableCell>
                            <TableCell>P. No.</TableCell>
                          </>
                        )}
                      </>
                    )}

                    {searchForm.esimType === EsimType.REDEMPTION && (
                      <>
                        <TableCell>Serial No.</TableCell>
                        <TableCell>ICCID</TableCell>
                        <TableCell>IMSI</TableCell>
                        <TableCell>MSISDN</TableCell>
                        <TableCell>Redemption Code</TableCell>
                        <TableCell>Activation Code</TableCell>
                        <TableCell>Confirm Code</TableCell>
                        <TableCell>Ext. eSIM ID</TableCell>
                        <TableCell>PIN</TableCell>
                        <TableCell>PUK</TableCell>
                        <TableCell>Expiry</TableCell>
                        {hasPassport && (
                          <>
                            <TableCell>P. Country</TableCell>
                            <TableCell>P. Name</TableCell>
                            <TableCell>P. Birthdate</TableCell>
                            <TableCell>P. Gender</TableCell>
                            <TableCell>P. No.</TableCell>
                          </>
                        )}
                      </>
                    )}

                    {searchForm.esimType === EsimType.DISCOVERY && (
                      <>
                        <TableCell>Activation Date</TableCell>
                        <TableCell>IMEI</TableCell>
                        <TableCell>EID</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Ext. eSIM ID</TableCell>
                        {hasPassport && (
                          <>
                            <TableCell>P. Country</TableCell>
                            <TableCell>P. Name</TableCell>
                            <TableCell>P. Birthdate</TableCell>
                            <TableCell>P. Gender</TableCell>
                            <TableCell>P. No.</TableCell>
                          </>
                        )}
                      </>
                    )}

                    {searchForm.esimType === EsimType.PSIM_TOPUP && (
                      <>
                        <TableCell>Serial No.</TableCell>
                        <TableCell>ICCID</TableCell>
                        <TableCell>IMSI</TableCell>
                        <TableCell>MSISDN</TableCell>
                        <TableCell>PIN</TableCell>
                        <TableCell>PUK</TableCell>
                        <TableCell>Status</TableCell>
                      </>
                    )}

                    {showUsageColumn && <TableCell align="center">Details</TableCell>}
                    {showRefundColumn && <TableCell align="center">Refund</TableCell>}
                  </TableRow>
                }
                rows={rows}
                getRowKey={(e) => e.esimId}
                columnsCount={columnsCount}
                isLoading={isLoading}
                loadingVariant="skeleton"
                skeletonRows={8}
                emptyText="No order items found."
                minWidth={1200}
                renderRow={(esim, index) => (
                  <>
                    <TableCell align="right" sx={cellSx.number}>
                      {(page - 1) * PAGE_SIZE + index + 1}
                    </TableCell>

                    {esim.esimType === EsimType.LPA && (
                      <>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.serialNo)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.iccid)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.imsi)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.msisdn)}</TableCell>
                        {renderActivationCodeCell(esim)}
                        <TableCell sx={cellSx.mono}>{renderValue(esim.confirmationCode)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.externalEsimId)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.pin)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.puk)}</TableCell>
                        <TableCell sx={cellSx.nowrap}>
                          {esim.expireAt ? timestampToDate(esim.expireAt, 'YYYY-MM-DD') : '-'}
                        </TableCell>
                        {hasPassport && (
                          <>
                            <TableCell>{renderValue(esim.customerDetail?.passportData?.passportCountry)}</TableCell>
                            <TableCell>{renderValue(esim.customerDetail?.passportData?.passportName)}</TableCell>
                            <TableCell>{renderValue(esim.customerDetail?.passportData?.passportBirthdate)}</TableCell>
                            <TableCell>{renderValue(esim.customerDetail?.passportData?.passportGender)}</TableCell>
                            <TableCell sx={cellSx.mono}>
                              {renderValue(esim.customerDetail?.passportData?.passportNumber)}
                            </TableCell>
                          </>
                        )}
                      </>
                    )}

                    {esim.esimType === EsimType.REDEMPTION && (
                      <>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.serialNo)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.iccid)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.imsi)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.msisdn)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.redemptionCode)}</TableCell>
                        {renderActivationCodeCell(esim)}
                        <TableCell sx={cellSx.mono}>{renderValue(esim.confirmationCode)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.externalEsimId)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.pin)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.puk)}</TableCell>
                        <TableCell sx={cellSx.nowrap}>
                          {esim.expireAt ? timestampToDate(esim.expireAt, 'YYYY-MM-DD') : '-'}
                        </TableCell>
                        {hasPassport && (
                          <>
                            <TableCell>{renderValue(esim.customerDetail?.passportData?.passportCountry)}</TableCell>
                            <TableCell>{renderValue(esim.customerDetail?.passportData?.passportName)}</TableCell>
                            <TableCell>{renderValue(esim.customerDetail?.passportData?.passportBirthdate)}</TableCell>
                            <TableCell>{renderValue(esim.customerDetail?.passportData?.passportGender)}</TableCell>
                            <TableCell sx={cellSx.mono}>
                              {renderValue(esim.customerDetail?.passportData?.passportNumber)}
                            </TableCell>
                          </>
                        )}
                      </>
                    )}

                    {esim.esimType === EsimType.DISCOVERY && (
                      <>
                        <TableCell sx={cellSx.nowrap}>
                          {esim.activationDate ? timestampToDate(esim.activationDate, 'YYYY-MM-DD') : '-'}
                        </TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.imei)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.eid)}</TableCell>
                        <TableCell>{renderValue(esim.customerName)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.externalEsimId)}</TableCell>
                        {hasPassport && (
                          <>
                            <TableCell>{renderValue(esim.customerDetail?.passportData?.passportCountry)}</TableCell>
                            <TableCell>{renderValue(esim.customerDetail?.passportData?.passportName)}</TableCell>
                            <TableCell>{renderValue(esim.customerDetail?.passportData?.passportBirthdate)}</TableCell>
                            <TableCell>{renderValue(esim.customerDetail?.passportData?.passportGender)}</TableCell>
                            <TableCell sx={cellSx.mono}>
                              {renderValue(esim.customerDetail?.passportData?.passportNumber)}
                            </TableCell>
                          </>
                        )}
                      </>
                    )}

                    {esim.esimType === EsimType.PSIM_TOPUP && (
                      <>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.serialNo)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.iccid)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.imsi)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.msisdn)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.pin)}</TableCell>
                        <TableCell sx={cellSx.mono}>{renderValue(esim.puk)}</TableCell>
                        <TableCell sx={cellSx.nowrap}>{renderValue(esim.esimStatus)}</TableCell>
                      </>
                    )}

                    {showUsageColumn && (
                      <TableCell align="center" sx={cellSx.nowrap}>
                        {esim.esimStatus === EsimStatus.CANCELED ? (
                          <Tooltip title="Details unavailable for canceled items">
                            <span>
                              <IconButton size="small" disabled>
                                <Insights fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="View status">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setUsagePopupVisible(true);
                                setSelectedEsim(esim);
                              }}
                            >
                              <Insights color="info" fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}

                    {showRefundColumn && (
                      <TableCell align="center" sx={cellSx.nowrap}>
                        {isRefundable && esim.esimStatus !== EsimStatus.CANCELED ? (
                          <Tooltip title="Cancel & refund">
                            <IconButton size="small" onClick={() => handleRefund(esim)}>
                              <Replay color="error" fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Refund unavailable">
                            <span>
                              <IconButton size="small" disabled>
                                <Replay fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </>
                )}
              />
            </Box>

            {/* Pagination */}
            <Box mt={2}>
              <StandardPagination
                page={page}
                pageSize={PAGE_SIZE}
                totalCount={total}
                onChangePage={handleChangePage}
                size="small"
                shape="rounded"
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Partial refund modal */}
      <Modal
        open={partialRefund}
        onClose={() => setPartialRefund(false)}
        width="min(720px, calc(100vw - 32px))"
        maxHeight="90vh"
      >
        <RefundEsimContent
          onClose={(shouldRefresh: boolean) => {
            setPartialRefund(false);
            if (shouldRefresh) {
              onRefund();
              refetch();
            }
          }}
          selectedEsim={selectedEsim}
          order={order}
        />
      </Modal>

      {/* Full refund modal */}
      <Modal
        open={fullRefund}
        onClose={() => setFullRefund(false)}
        width="min(720px, calc(100vw - 32px))"
        maxHeight="90vh"
      >
        <FullRefundEsimContent
          onClose={(shouldRefresh: boolean) => {
            setFullRefund(false);
            if (shouldRefresh) {
              onRefund();
              refetch();
            }
          }}
          order={order}
          totalCount={order.reservedAt ? order.quantity : total || 0}
        />
      </Modal>

      {/* Usage modal */}
      <Modal open={usagePopupVisible} onClose={() => setUsagePopupVisible(false)} width="1200px">
        <OrderEsimUsageContent
          onClose={() => setUsagePopupVisible(false)}
          order={order}
          esim={selectedEsim}
          visible={usagePopupVisible}
        />
      </Modal>

      <EsimQrCodeDialog
        open={Boolean(qrEsim)}
        onClose={() => setQrEsim(null)}
        activationCode={qrEsim?.lpaUrl}
        confirmationCode={qrEsim?.confirmationCode}
        onCopy={handleCopyQrValue}
      />
    </>
  );
};

export default EsimListTable;
